import {
  CustomTooltip,
  DatePickerInput,
  CustomButton,
  CustomCheckbox,
} from '@/shared/ui/components';
import { closeWhiteIcon, guideLineIcon, reloadIcon } from '@/shared/ui/assets';
import { Checkbox } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useEcgOptionsComponent } from './useEcgOptionsComponent';
import IndicatorIcon from './assets/arrow-dropdown.svg';
import LeftIcon from './assets/arrow-left.svg';
import RightIcon from './assets/arrow-right.svg';
import NoDataEventIcon from './assets/no-data-event-icon.svg';
import type { EcgOptionsComponentProps } from './ecgOptionsComponent.types';
import './styles.scss';

/**
 * Stands in for optional ECG option callbacks that the component does not need to handle. Takes no arguments, runs no statements and produces no value.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

dayjs.extend(utc);

const EcgOptionsComponent = ({
  isCheckedEventIndicators = false,
  handleCheckEventIndicators = NOOP,
  handleClickArrow = NOOP,
  selectedDate,
  handleChangeDate,
  aboveLimit,
  belowLimit,
  isLoadedData,
  onClickReload,
  hasNoData,
  toggleGuideLineDrawer,
}: EcgOptionsComponentProps) => {
  const { state, isInitial, onChangeCheckbox, onClickConfirm } = useEcgOptionsComponent({
    isLoadedData,
  });

  return isLoadedData || !isInitial.current ? (
    <div className="ecg-options">
      <div className="options">
        <div className="left-option">
          <div className="select-date">
            <CustomButton
              size="small"
              style="neutral"
              type="outline"
              id="previous-date-btn"
              disabled={
                dayjs(dayjs(selectedDate).format('MM/DD/YYYY')).startOf('day').valueOf() ===
                  belowLimit || !isLoadedData
              }
              icon={<ReactSVG src={LeftIcon} />}
              onClick={() => {
                handleClickArrow(false);
              }}
              ariaLabel={t`Previous day`}
            />
            <DatePickerInput
              id="selected-date-input"
              name="selectedDate"
              ariaLabel={t`Select date`}
              className="select-date__input"
              value={selectedDate}
              onChange={handleChangeDate}
              suffixIcon={<img src={IndicatorIcon} alt="Indicator icon" />}
              allowClear={false}
              aboveLimit={aboveLimit}
              belowLimit={belowLimit}
              disabled={!isLoadedData}
              inputReadOnly
            />
            <CustomButton
              size="small"
              id="next-date-btn"
              style="neutral"
              type="outline"
              disabled={
                dayjs(dayjs(selectedDate).format('MM/DD/YYYY')).endOf('day').valueOf() ===
                  aboveLimit || !isLoadedData
              }
              icon={<ReactSVG src={RightIcon} />}
              onClick={() => {
                handleClickArrow(true);
              }}
              ariaLabel={t`Next day`}
            />
          </div>
          <CustomCheckbox
            id="event-indicators"
            {...({ name: 'eventIndicators' } as any)}
            checked={isCheckedEventIndicators}
            onChange={handleCheckEventIndicators}
            className="checkbox__input"
            label={t`Show event indicators`}
            disabled={!isLoadedData || hasNoData}
          />
        </div>

        <div className="right-option">
          <CustomButton
            type="secondary"
            style="default"
            size="small"
            id="reload-data-id-btn"
            icon={<ReactSVG src={reloadIcon} />}
            disabled={!isLoadedData}
            onClick={onClickReload}
          >
            {t`Refresh`}
          </CustomButton>
          <CustomTooltip
            title={
              <div className="guideline-container">
                <div className="content">
                  <div>{t`Click here to access the ECG viewer Guideline.`}</div>
                  <img src={closeWhiteIcon} alt="closeWhiteIcon" onClick={onClickConfirm} />
                </div>
                <div>
                  <Checkbox
                    id="guideline-do-not-show-checkbox"
                    name="doNotShowGuideline"
                    className="check-box"
                    checked={state.isCheck}
                    onChange={onChangeCheckbox}
                  >
                    {t`Do not show this again`}
                  </Checkbox>
                </div>
              </div>
            }
            classNames={{ root: 'guideline-popup' }}
            trigger="click"
            open={state.isOpenNotiPopover}
            getPopupContainer={(ele) => ele}
            // onOpenChange={onClickConfirm}
            placement="bottomRight"
          >
            <CustomButton
              id="guide-line-id-btn"
              type="secondary"
              style="default"
              size="small"
              icon={<ReactSVG src={guideLineIcon} />}
              disabled={!isLoadedData}
              onClick={toggleGuideLineDrawer}
              ariaLabel={t`ECG viewer guideline`}
            />
          </CustomTooltip>
        </div>
      </div>

      <div className="separator-line" />

      <div className="event-info-container">
        <div className="event-info-box --event">
          <div className="event-box" />
          <div>{t`Event`}</div>
        </div>
        <div className="event-info-box --no-data">
          <img src={NoDataEventIcon} alt="No data event icon" />
          <div>{t`No data`}</div>
        </div>
      </div>
    </div>
  ) : (
    <div className="navigator-skeleton-container">
      <div className="skeleton-container">
        <Skeleton height="100%" />
      </div>
    </div>
  );
};

export default EcgOptionsComponent;
